/**
 * Reports redesign — KPI corner sparkline. Tiny SVG line chart with
 * a subtle area-fill gradient underneath the stroke. Renders nothing
 * when `values` is empty, so callers can safely pass derived arrays
 * without guarding upstream.
 *
 * Used inside the absolute-positioned slot at the top-right of each
 * KPI card; size defaults to 76×28 to match the prototype's
 * `.v1-rep-kpi-spark` rect.
 */
export interface SparklineProps {
  values: ReadonlyArray<number>;
  color?: string;
  width?: number;
  height?: number;
  className?: string;
}

export const Sparkline = ({
  values,
  color = 'oklch(0.6 0.1 245)',
  width = 76,
  height = 28,
  className,
}: SparklineProps) => {
  if (!values || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const step = width / Math.max(1, values.length - 1);
  // Stable per-instance gradient id so multiple sparklines on the
  // same page don't all reference the same `<defs>` block — last
  // one wins otherwise. Uses a hash of the values + color so two
  // identical sparks share the same id (cheap dedup).
  const gradId = `spark-${Math.abs(
    [...values, color].reduce<number>((acc, v) => (acc * 31 + (typeof v === 'number' ? v : v.length)) | 0, 5381),
  )}`;

  const points = values.map(
    (v, i) =>
      `${(i * step).toFixed(1)},${(height - (v / max) * height).toFixed(1)}`,
  );
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${width},${height} L 0,${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
